/**
 * Fair Home V2 hero — Figma Inspect values in design units
 * (desktop frame 1440×900 `15:9503` · compact 393×852 `15:9523`).
 *
 * Collision rule (2026-09-09): no tagline may sit inside the wordmark box or
 * the rail zone, at rest or at its +99 entrance offset.
 *   desktop: wordmark x 278–1162 · y 200–392 · rail x 210–1230 · y ≥ 404
 *   compact: wordmark x 28–365 · y 397–470 · rail full width · y ≥ 482
 * Anchors marked "moved" deviate from Figma for that reason; the rest are
 * verbatim. Compact alignments come from the animation screen `15:9513`.
 */

export interface FairHomeV2HeroTaglineAnchor {
  top: string
  left?: string
  /** Right pin (frame width − node x − node w) for right-edge texts. */
  right?: string
  width: string
  /** Figma paragraph alignment (default LEFT). */
  align?: 'start' | 'end'
}

export interface FairHomeV2HeroTagline {
  id: string
  lines: readonly [string, ...string[]]
  desktop: FairHomeV2HeroTaglineAnchor
  compact: FairHomeV2HeroTaglineAnchor
}

export const FAIR_HOME_V2_HERO_TAGLINES: readonly FairHomeV2HeroTagline[] = [
  {
    // #15:9511 — x196 y207 w210 (moved: x48, clears wordmark x278)
    // compact #15:9519 — x155 y482 (moved: y340 x23, was on the hangers)
    id: 'learns',
    lines: ['FAIR learns what you love'],
    desktop: { top: '207px', left: '48px', width: '210px' },
    compact: { top: '340px', left: '23px', width: '210px' },
  },
  {
    // #15:9510 — x1086 y189 w246 → right 108 (moved: right 40 w230 → x1170+,
    // clears wordmark x1162; wraps to 2 lines)
    // compact #15:9518 — x76 y371 w246 h18
    id: 'less-searching',
    lines: ['Less searching. More wearing.'],
    desktop: { top: '189px', right: '40px', width: '230px' },
    compact: { top: '371px', left: '76px', width: '246px' },
  },
  {
    // #15:9508 — x90 y450 w128 h36 · compact #15:9516 — x23 y268 w128 h36
    id: 'one-wardrobe',
    lines: ['One wardrobe.', 'Every brand.'],
    desktop: { top: '450px', left: '90px', width: '128px' },
    compact: { top: '268px', left: '23px', width: '128px' },
  },
  {
    // #15:9509 — x1231 y499 w146 h36 → right pin 1440−1231−146=63
    // compact #15:9517 — x219 y301 w146 h36, RIGHT-aligned
    id: 'build-wardrobe',
    lines: ['Build a wardrobe', 'that works.'],
    desktop: { top: '499px', right: '63px', width: '146px' },
    compact: { top: '301px', left: '219px', width: '146px', align: 'end' },
  },
  {
    // #15:9506 — x48 y684 w148 h36 · compact #15:9514 — x23 y426 (moved: y208,
    // was inside the wordmark box 397–470)
    id: 'your-size',
    lines: ['Your size, in every', "brand's language"],
    desktop: { top: '684px', left: '48px', width: '148px' },
    compact: { top: '208px', left: '23px', width: '148px' },
  },
  {
    // #15:9507 — x1254 y786 w130 h36 → right pin 1440−1254−130=56
    // compact #15:9515 — x134 y160 w130 h36, RIGHT-aligned
    id: 'find-style',
    lines: ['Find your style,', 'not just clothes.'],
    desktop: { top: '786px', right: '56px', width: '130px' },
    compact: { top: '160px', left: '134px', width: '130px', align: 'end' },
  },
] as const
